import React, { useRef, useState } from 'react';
import moment from 'moment-timezone';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { dialogStore } from 'components/layout/modals/DialogStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { observer } from 'mobx-react';
import { jobApplicationStore } from './JobApplicationStore';
import { applicantStore } from 'ats/pages/applicants/ApplicantStore';
import ColumnSelector from 'ats/components/column-selector/ColumnSelector';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import { formatTimeAgo } from 'shared/utils';
import { candidateSeniorityOptionLabel } from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { eventManager } from 'ats/utils/EventManager';
import EllipsisTooltipLabel from 'ats/components/ellipsis-tooltip-label/EllipsisTooltipLabel';
import InternalStageCell from 'ats/components/internal-stage-cell/InternalStageCell';
import BulkActionsBar from 'ats/components/bulk-actions-bar/BulkActionsBar';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import LatestTaskCell from 'ats/components/latest-task-cell/LatestTaskCell';
import AtsTableSkeleton from 'ats/components/ats-table-skeleton/AtsTableSkeleton';
import { modalStore } from 'components/layout/modals/ModalStore';
import { AssignToJobModal } from 'ats/pages/applicants/components/AssignToJobModal';
import JobApplicationStageLabel from './JobApplicationStageLabel';
import ApplicantStageIndicator from 'ats/components/applicant-stage-indicator/ApplicantStageIndicator';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';
import Dropdown from 'ats/components/dropdown/Dropdown';
import { getJobApplicationStageTheme } from 'ats/constants/jobApplicationStageTheme';

const DIALOG_CONFIGS = {
	DELETE: {
		title: 'Delete Job Application',
		message: 'This Job Application entry will be deleted. Are you sure?',
		confirmLabel: 'Delete',
		confirmButtonClass: 'is-danger'
	}
};

function JobsApplicationsTable({
	data,
	onClickRow,
	activeRowId,
	sort,
	onSort,
	onSuccess,
	visibleColumns,
	onVisibleColumnsChange,
	loading
}) {
	const [selectedIds, setSelectedIds] = useState([]);
	const [menuOpenRowId, setMenuOpenRowId] = useState(null);
	const suppressRowClickRef = useRef(false);

	const getStage = value =>
		jobApplicationStore.stageOptions.find(stage => stage.value === value);

	const getStatus = value =>
		jobApplicationStore.statusOptions.find(status => status.value === value);

	const getApplicantStage = value =>
		applicantStore.stageOptions.find(stage => stage.value === value);

	const getApplicantStatus = value =>
		applicantStore.statusOptions.find(status => status.value === value);

	const getEnglishLevel = value =>
		candidatesStore.englishLevelOptions.find(level => level.value === value);

	const isColumnSorted = column => {
		if (!sort || sort.column !== column) return null;
		const icon = sort.asc ? 'arrow-up-icon' : 'arrow-down-icon';
		return (
			<span className="sort-icon">
				<img src={`/ats/icons/${icon}.svg`} alt="" />
			</span>
		);
	};

	const handleHeaderClick = column => {
		onSort?.(column);
	};

	const resolvedVisibleColumns =
		visibleColumns || jobApplicationStore.defaultTableColumnsOptions;

	const shouldShowColumn = column =>
		resolvedVisibleColumns.some(
			visibleColumn => visibleColumn.value === column
		);

	const handleSelectAll = e => {
		if (e.target.checked) {
			setSelectedIds(data.map(row => row.id));
		} else {
			setSelectedIds([]);
		}
	};

	const handleSelectRow = (e, rowId) => {
		e.stopPropagation();
		if (e.target.checked) {
			setSelectedIds(prev => [...prev, rowId]);
		} else {
			setSelectedIds(prev => prev.filter(id => id !== rowId));
		}
	};

	const isAllSelected = data.length > 0 && selectedIds.length === data.length;
	const isPartiallySelected =
		selectedIds.length > 0 && selectedIds.length < data.length;

	const handleJobApplicationDelete = jobApplication => {
		dialogStore.openDialog(DIALOG_CONFIGS.DELETE, () =>
			jobApplicationStore.delete(jobApplication.id).then(result => {
				if (result.status === 'success') {
					notificationStore.pushSuccessNotification(
						'Job Application Deleted',
						'The Job Application has been successfully deleted.'
					);
					onSuccess?.();

					eventManager.emit('jobApplicationDeleted', {
						jobApplicationId: jobApplication.id,
						applicantId: jobApplication.applicant.id
					});
				} else {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete the Job Application'
					);
				}
			})
		);
	};

	const handleApplicationStageChange = async (row, stage) => {
		// Portal menu clicks can fall through to the row after unmount and open
		// the candidate drawer; ignore row clicks briefly while stage updates.
		suppressRowClickRef.current = true;
		window.setTimeout(() => {
			suppressRowClickRef.current = false;
		}, 400);

		await jobApplicationStore.update(row.id, {
			jobApplication: { stage: stage.value }
		});
		onSuccess?.();

		eventManager.emit('jobApplicationStageChanged', {
			jobApplicationId: row.id,
			stage: stage.value,
			applicantId: row.applicant.id,
			jobId: row.job.id
		});
	};

	const getApplicationStageDropdownOptions = () =>
		jobApplicationStore.stageOptions.map(stage => {
			const statusData = getStatus(
				jobApplicationStore.stageToStatus[stage.value]
			);

			return {
				...stage,
				label: (
					<JobApplicationStageLabel stageData={stage} statusData={statusData} />
				)
			};
		});

	const handleBulkChangeStage = async stage => {
		try {
			const result = await jobApplicationStore.bulkUpdate(selectedIds, {
				stage: stage.value
			});
			if (result.status === 'success') {
				notificationStore.pushSuccessNotification(
					'Stage Updated',
					`Successfully updated ${result.data.updated} application(s) to ${stage.label}`
				);
				const selectedData = data.filter(row => selectedIds.includes(row.id));
				selectedData.forEach(row => {
					eventManager.emit('jobApplicationStageChanged', {
						jobApplicationId: row.id,
						stage: stage.value,
						applicantId: row.applicant.id,
						jobId: row.job.id
					});
				});
				onSuccess?.();
			} else {
				notificationStore.pushErrorNotification(
					'Update Failed',
					'Failed to update applications'
				);
			}
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Update Failed',
				'Failed to update some applications'
			);
		}
	};

	const getSelectedApplicantIds = () => {
		const selectedRows = data.filter(row => selectedIds.includes(row.id));
		return [...new Set(selectedRows.map(row => row.applicant.id))];
	};

	const handleBulkAssignToAnotherJob = () => {
		modalStore.openModal(
			{
				title: 'Assign to an Open Job',
				position: 'center',
				size: 'md'
			},
			<AssignToJobModal
				applicantIds={getSelectedApplicantIds()}
				onSuccess={() => {
					onSuccess?.();
				}}
			/>
		);
	};

	const handleBulkDelete = () => {
		dialogStore.openDialog(
			{
				title: 'Delete Job Applications',
				message: `Are you sure you want to delete ${selectedIds.length} job application(s)? This action cannot be undone.`,
				confirmLabel: 'Delete',
				confirmButtonClass: 'is-danger'
			},
			async () => {
				try {
					const selectedData = data.filter(row => selectedIds.includes(row.id));
					const result = await jobApplicationStore.bulkDelete(selectedIds);
					if (result.status === 'success') {
						notificationStore.pushSuccessNotification(
							'Job Applications Deleted',
							`Successfully deleted ${result.data.deleted} job application(s)`
						);
						selectedData.forEach(row => {
							eventManager.emit('jobApplicationDeleted', {
								jobApplicationId: row.id,
								applicantId: row.applicant.id
							});
						});
						setSelectedIds([]);
						onSuccess?.();
					} else {
						notificationStore.pushErrorNotification(
							'Delete Failed',
							'Failed to delete job applications'
						);
					}
				} catch (error) {
					notificationStore.pushErrorNotification(
						'Delete Failed',
						'Failed to delete some job applications'
					);
				}
			}
		);
	};

	const getBulkActions = () => {
		const stageItems = jobApplicationStore.stageOptions.map(stage => {
			const statusData = getStatus(
				jobApplicationStore.stageToStatus[stage.value]
			);
			return {
				label: (
					<JobApplicationStageLabel stageData={stage} statusData={statusData} />
				),
				onClick: () => handleBulkChangeStage(stage)
			};
		});

		return [
			{
				label: 'Change Application Stage',
				items: stageItems
			},
			{
				label: 'Assign to another Job',
				onClick: handleBulkAssignToAnotherJob
			},
			{
				label: 'Delete',
				isDanger: true,
				onClick: handleBulkDelete
			}
		];
	};

	const getMenuItems = row => [
		{
			label: 'Change Application Stage',
			subItems: getApplicationStageDropdownOptions()
				.filter(stage => stage.value !== row?.stage)
				.map(stage => ({
					...stage,
					onClick: () => handleApplicationStageChange(row, stage)
				}))
		},
		{
			label: 'Unassign',
			onClick: () => handleJobApplicationDelete(row)
		}
	];

	const oneWeekAgo = moment().subtract(1, 'week');

	const isProfileOpen =
		drawerStore.isOpen &&
		[
			drawerStore.DRAWER_IDS.JOB_POSITION_PROFILE,
			drawerStore.DRAWER_IDS.APPLICANT_PROFILE,
			drawerStore.DRAWER_IDS.CLIENT_PROFILE
		].includes(drawerStore.drawerId);

	const renderTableHeader = () => (
		<thead>
			<tr>
				<th style={{ width: '3px', padding: 0 }}></th>
				<th style={{ width: '35px' }}>
					<input
						type="checkbox"
						checked={isAllSelected}
						ref={input => {
							if (input) {
								input.indeterminate = isPartiallySelected;
							}
						}}
						onChange={handleSelectAll}
					/>
				</th>
				{shouldShowColumn('name') && (
					<th
						className="is-sortable name-column-header"
						onClick={() => handleHeaderClick('applicant.name')}>
						<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
							<span
								className="tooltip is-tooltip-top pipeline-jobs-th-label"
								data-tooltip="Name">
								Name
							</span>
							{isColumnSorted('applicant.name')}
						</div>
					</th>
				)}
				{shouldShowColumn('internal_stage') && !isProfileOpen && (
					<th
						className="is-sortable has-text-left"
						onClick={() => handleHeaderClick('applicant.stage')}>
						<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
							<span
								className="tooltip is-tooltip-top pipeline-jobs-th-label"
								data-tooltip="Internal Stage">
								Internal Stage
							</span>
							{isColumnSorted('applicant.stage')}
						</div>
					</th>
				)}
				{shouldShowColumn('stage') && (
					<th
						className="is-sortable stage-column"
						onClick={() => handleHeaderClick('stage')}>
						<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
							<span
								className="tooltip is-tooltip-top pipeline-jobs-th-label"
								data-tooltip="Application Stage">
								Application Stage
							</span>
							{isColumnSorted('stage')}
						</div>
					</th>
				)}
				{shouldShowColumn('latest_task') && !isProfileOpen && (
					<th>
						<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
							<span
								className="tooltip is-tooltip-top pipeline-jobs-th-label"
								data-tooltip="Latest Task">
								Latest Task
							</span>
						</div>
					</th>
				)}
				{isProfileOpen ? null : (
					<>
						{shouldShowColumn('current_compensation') && (
							<th
								className="is-sortable"
								onClick={() =>
									handleHeaderClick('applicant.candidate.$currentCompensation')
								}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Current Compensation">
										Current Compensation
									</span>
									{isColumnSorted('applicant.candidate.$currentCompensation')}
								</div>
							</th>
						)}
						{shouldShowColumn('expected_compensation') && (
							<th
								className="is-sortable expected-compensation-column-header has-text-left"
								onClick={() =>
									handleHeaderClick('applicant.candidate.$expectedCompensation')
								}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<EllipsisTooltipLabel
										wrapperClassName="pipeline-expected-compensation-header-tooltip"
										className="pipeline-expected-compensation-header-label"
										text="Expected Compensation"
									/>
									{isColumnSorted('applicant.candidate.$expectedCompensation')}
								</div>
							</th>
						)}
						{shouldShowColumn('seniority') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('applicant.seniority')}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Seniority">
										Seniority
									</span>
									{isColumnSorted('applicant.seniority')}
								</div>
							</th>
						)}
						{shouldShowColumn('english_level') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('applicant.englishLevel')}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="English Level">
										English Level
									</span>
									{isColumnSorted('applicant.englishLevel')}
								</div>
							</th>
						)}
						{shouldShowColumn('country') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('applicant.country.name')}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Country">
										Country
									</span>
									{isColumnSorted('applicant.country.name')}
								</div>
							</th>
						)}
						{shouldShowColumn('owner') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('applicant.owner.name')}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Owner">
										Owner
									</span>
									{isColumnSorted('applicant.owner.name')}
								</div>
							</th>
						)}
						{shouldShowColumn('source') && (
							<th
								className="is-sortable has-text-centered"
								onClick={() => handleHeaderClick('applicant.source.name')}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Source">
										Source
									</span>
									{isColumnSorted('applicant.source.name')}
								</div>
							</th>
						)}
						{shouldShowColumn('created_at') && (
							<th
								className="is-sortable"
								onClick={() => handleHeaderClick('createdAt')}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Created At">
										Created At
									</span>
									{isColumnSorted('createdAt')}
								</div>
							</th>
						)}
						{shouldShowColumn('last_engagement_date') && (
							<th
								className="is-sortable"
								onClick={() =>
									handleHeaderClick('applicant.$lastEngagementAt')
								}>
								<div className="is-flex is-align-items-center pipeline-jobs-th-inner">
									<span
										className="tooltip is-tooltip-top pipeline-jobs-th-label"
										data-tooltip="Last Engagement Date">
										Last Engagement Date
									</span>
									{isColumnSorted('applicant.$lastEngagementAt')}
								</div>
							</th>
						)}
					</>
				)}
				<th className="has-text-centered column-selector-column action-btn">
					{isProfileOpen ? null : (
						<ColumnSelector
							target={props => (
								<button type="button" className="action-btn" {...props}>
									<img src="/ats/icons/plus-icon.svg" alt="Add button" />
								</button>
							)}
							value={resolvedVisibleColumns}
							options={jobApplicationStore.tableColumnsOptions}
							onChange={values => onVisibleColumnsChange?.(values)}
						/>
					)}
				</th>
				<th className="pending-task-column"></th>
			</tr>
		</thead>
	);

	const renderTableRow = row => {
		const stageData = getStage(row.stage);
		const statusData = getStatus(row.status);
		const applicantStageData = getApplicantStage(row.applicant.stage);
		const applicantStatusData = getApplicantStatus(row.applicant.status);

		const lastEngagementAt = row.applicant?.lastEngagementAt;
		const shouldShowActivityAlert =
			lastEngagementAt && moment(lastEngagementAt).isBefore(oneWeekAgo);
		const isSelected = selectedIds.includes(row.id);

		return (
			<tr
				key={row.id}
				className={`${isProfileOpen && String(activeRowId) === String(row.id) ? 'is-active' : ''} ${isSelected ? 'is-selected' : ''} ${menuOpenRowId === row.id ? 'is-menu-open' : ''}`}
				onClick={event => {
					event.stopPropagation();

					if (suppressRowClickRef.current) {
						return;
					}

					if (
						['BUTTON', 'INPUT'].includes(event.target.tagName) ||
						event.target.closest('.dropdown-ats') ||
						event.target.closest('.dropdown-ats-menu') ||
						event.target.closest('.job-application-stage-dropdown-menu') ||
						event.target.closest('.hover-menu')
					) {
						return;
					}

					onClickRow?.(row);
				}}>
				<td
					className={`activity-alert-column ${shouldShowActivityAlert ? 'alert' : ''}`}>
					{shouldShowActivityAlert && (
						<AtsTooltip
							text={`Last Activity: ${formatTimeAgo(new Date(lastEngagementAt))}`}
							placement="right">
							<div className="activity-alert-indicator"></div>
						</AtsTooltip>
					)}
				</td>
				<td className="checkbox-column">
					<input
						type="checkbox"
						checked={isSelected}
						onChange={e => handleSelectRow(e, row.id)}
					/>
				</td>
				{shouldShowColumn('name') && (
					<td className="name-column">
						<div className="is-flex is-align-items-center gap-2">
							{isProfileOpen && (
								<ApplicantStageIndicator
									stageLabel={applicantStageData?.label}
									statusLabel={applicantStatusData?.label}
									stageLevel={applicantStageData?.level}
									statusColor={applicantStatusData?.color}
								/>
							)}
							<EllipsisTooltipLabel
								wrapperClassName="candidate-name-wrap"
								className="candidate-name"
								text={row.applicant.name || ''}
								tooltipPlacement="top"
							/>
						</div>
					</td>
				)}
				{shouldShowColumn('internal_stage') && !isProfileOpen && (
					<td className="internal-stage-column">
						<InternalStageCell
							stageLabel={applicantStageData?.label}
							statusLabel={applicantStatusData?.label}
							stageLevel={applicantStageData?.level}
							statusColor={applicantStatusData?.color}
						/>
					</td>
				)}
				{shouldShowColumn('stage') && (
					<td className="is-capitalized stage-column">
						<Dropdown
							value={{
								...stageData,
								label: (
									<JobApplicationStageLabel
										stageData={stageData}
										statusData={statusData}
										showStatusTooltip
										truncate
									/>
								)
							}}
							buttonTextColor={
								getJobApplicationStageTheme(stageData?.value).color
							}
							menuClassName="job-application-stage-dropdown-menu"
							hideSelectedOption
							onChange={stage => handleApplicationStageChange(row, stage)}
							options={getApplicationStageDropdownOptions()}
						/>
					</td>
				)}
				{shouldShowColumn('latest_task') && !isProfileOpen && (
					<td className="latest-task-column">
						<LatestTaskCell latestTask={row.latestTask} showJobTitle={false} />
					</td>
				)}
				{isProfileOpen ? null : (
					<>
						{shouldShowColumn('current_compensation') && (
							<td className="is-capitalized">
								{row.applicant?.currentCompensation
									? `${row.applicant?.currentCurrency} ${row.applicant?.currentCompensation}/${row.applicant?.currentScheme === 'hourly' ? 'Hr' : 'Mo'}`
									: '-'}
							</td>
						)}
						{shouldShowColumn('expected_compensation') && (
							<td className="is-capitalized">
								{row.applicant?.expectedCompensation
									? `${row.applicant?.expectedCurrency} ${row.applicant?.expectedCompensation}/${row.applicant?.expectedScheme === 'hourly' ? 'Hr' : 'Mo'}`
									: '-'}
							</td>
						)}
						{shouldShowColumn('seniority') && (
							<td className="is-capitalized">
								{candidateSeniorityOptionLabel[row.applicant.seniority]}
							</td>
						)}
						{shouldShowColumn('english_level') && (
							<td className="is-capitalized">
								{getEnglishLevel(row.applicant?.englishLevel)?.text}
							</td>
						)}
						{shouldShowColumn('country') && (
							<td className="is-capitalized">{row.applicant?.country?.name}</td>
						)}
						{shouldShowColumn('owner') && (
							<td className="owner-column">
								{row.applicant.owner && (
									<UserProfileAvatar
										imageUrl={row.applicant.owner?.imageUrl}
										name={row.applicant.owner?.name}
									/>
								)}
							</td>
						)}
						{shouldShowColumn('source') && (
							<td className="source-column">
								{row.applicant.source && (
									<UserProfileAvatar
										imageUrl={row.applicant.source?.imageUrl}
										name={row.applicant.source?.name}
									/>
								)}
							</td>
						)}
						{shouldShowColumn('created_at') && (
							<td>
								{row.createdAt ? moment(row.createdAt).format('lll') : '-'}
							</td>
						)}
						{shouldShowColumn('last_engagement_date') && (
							<td>
								{row.applicant?.lastEngagementAt
									? moment(row.applicant.lastEngagementAt).format('lll')
									: '-'}
							</td>
						)}
					</>
				)}
				<td className="has-text-centered action-btn">
					<HoverMenu
						id={row.id}
						position="left"
						buttonIcon={
							<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
						}
						items={getMenuItems(row)}
						onOpenChange={open => setMenuOpenRowId(open ? row.id : null)}
					/>
				</td>
				<td className="pending-task-column">
					<PendingTaskBadge count={row.pendingTasksCount} variant="dot" />
				</td>
			</tr>
		);
	};

	return (
		<>
			<BulkActionsBar
				selectedCount={selectedIds.length}
				actions={getBulkActions()}
				onClearSelection={() => setSelectedIds([])}
			/>
			<div className="ats-table-scroll">
				<table
					className={`table is-fullwidth${isProfileOpen ? ' is-short-list-view' : ''}`}>
					{renderTableHeader()}
					{loading && data.length === 0 ? (
						<AtsTableSkeleton
							columns={Math.max(visibleColumns?.length || 0, 4)}
						/>
					) : (
						<tbody>{data.map(renderTableRow)}</tbody>
					)}
				</table>
			</div>
		</>
	);
}

export default observer(JobsApplicationsTable);
